/*
PURPOSE: Editorial 404 page. Also the router-level errorElement
(routerConfig.tsx App root) — i.e. the app-wide crash fallback.
USE CASES:
- Unknown route, and any uncaught render/loader error on any route.
DOMAIN BOUNDARIES:
- Presentation only. Copy preserved verbatim from the prior implementation
  (incl. existing wording) — no rewrites.
- Because this doubles as the global crash screen it must render with ZERO
  new runtime dependencies: only the stable editorial primitives, the
  already-imported CoralEcosystem, and react-router Link. No data fetch, no
  removed-asset import, no not-yet-defined token, no loader-data assumption.
- CoralEcosystem is kept as-is (shared component; bloom generalisation is an
  out-of-scope follow-up) inside editorial chrome.
- No imports from design-sandbox/.
*/

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import CoralEcosystem from '../../../components/CoralEcosystem/CoralEcosystem';
import { EditorialSection, Eyebrow, DisplayHeading, BodyProse } from '../../../components/editorial';

const NotFound: React.FC = () => {
  return (
    <div className="flex flex-col min-h-screen bg-ivory">
      <EditorialSection background="ivory" topRule={false} id="not-found">
        <DisplayHeading as="h1" scale="display">
          Page Not Found
        </DisplayHeading>
        <BodyProse className="mt-6">
          <p>We couldn&apos;t find the page you&apos;re looking for.</p>
        </BodyProse>
        <div className="mt-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-coral-orange hover:bg-coral-orange/90 text-editorial-muted rounded-full px-6 py-3 text-sm font-medium transition-colors"
          >
            <span>Return Home</span>
            <ArrowRight size={16} strokeWidth={2} />
          </Link>
        </div>
      </EditorialSection>

      <EditorialSection background="cream" id="not-found-vision">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <Eyebrow>OUR VISION</Eyebrow>
            <DisplayHeading as="h2" scale="heading" className="mt-8">
              A thriving ecosystem where music is valued and diverse styles and scenes can prosper.
            </DisplayHeading>
          </div>
          <div>
            <CoralEcosystem />
          </div>
        </div>
      </EditorialSection>
    </div>
  );
};

export default NotFound;
